import type { DragEventHandler, MouseEventHandler, ReactNode } from 'react';

import { cx } from '@/utils/cx';

export interface DropAreaProps {
  /** Content rendered inside the drop zone. */
  children: ReactNode;
  /** Whether something is currently hovering over this area. */
  isOver: boolean;
  /** Ref callback from `useDroppable().setNodeRef`. */
  nodeRef?: (node: HTMLElement | null) => void;
  /**
   * Show a visible dashed border at rest (e.g. for a file upload zone or
   * an empty drop target). When false (default), the resting border is
   * transparent — space is still reserved so content doesn't shift on hover.
   */
  showBorder?: boolean;
  /** Additional className on the outer wrapper. */
  className?: string;
  /** Accessible name for the drop zone, announced by screen readers during drag operations. */
  ariaLabel?: string;
  /** Native drag event handlers (for file drop zones that use browser drag). */
  onDrop?: DragEventHandler;
  onDragOver?: DragEventHandler;
  onDragLeave?: DragEventHandler;
  onClick?: MouseEventHandler;
}

/**
 * DropArea — a visual wrapper that highlights when a draggable hovers over it.
 *
 * Resting state: a 2px dashed border (transparent by default, or visible when
 * `showBorder` is true). On hover: the border turns brand + a light brand
 * background tint — matching the `FileDropZone` upload pattern.
 *
 * Purely presentational — does NOT call `useDroppable`. The consumer wires up
 * their drag library and passes `isOver` through.
 */
export function DropArea({
  children,
  isOver,
  nodeRef,
  showBorder = false,
  className,
  ariaLabel,
  onDrop,
  onDragOver,
  onDragLeave,
  onClick,
}: DropAreaProps) {
  return (
    <div
      ref={nodeRef}
      aria-label={ariaLabel}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onClick={onClick}
      className={cx(
        'rounded-lg border-2 border-dashed transition-colors',
        // Drop-target highlight: focus-ring token (theme-aware interaction
        // highlight) + auto-inverting utility tint — the raw brand pair +
        // hand-written dark: overrides double-flipped in dark mode.
        isOver && 'border-focus-ring bg-utility-brand-50',
        !isOver && showBorder && 'border-border-secondary',
        !isOver && !showBorder && 'border-transparent',
        className
      )}
    >
      {children}
    </div>
  );
}
